import type { OrganizationAssets } from "@frontend/lib/graphql/generated-gql";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { Button } from "@frontend/shared/components/ui/button";
import { Input } from "@frontend/shared/components/ui/input";
import { Label } from "@radix-ui/react-dropdown-menu";
import {
  APIProvider,
  AdvancedMarker,
  Map as GoogleMap,
  Pin,
} from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useRef, useState } from "react";
import { useZipCodeGeocode } from "../geocoding-utils";
import { calculateViewport, coordPattern } from "../utils";
import { LocationCard } from "./LocationCard";
import { LocationListWithDistance } from "./LocationListWithDistance";

const API_KEY = process.env.NEXT_PUBLIC_GCP_MAPS_API as string;

export interface Location {
  address?: string;
  label: string;
  value: string;
  phoneNumber?: string | null;
  googleMapsEmbed: string;
  distance?: number;
}

interface LocationSelectorProps {
  experityBooking: boolean;
  organizationAssets: OrganizationAssets | null | undefined;
  locations: Location[];
  selectedPracticeId: number;
  onSelectLocation: (id: number) => void;
}

export function hasAnyMapCoordinates(locations: Location[]): boolean {
  return locations.some(
    (loc) => loc.googleMapsEmbed?.match(coordPattern) != null,
  );
}

export function LocationSelector({
  experityBooking,
  organizationAssets,
  locations,
  selectedPracticeId,
  onSelectLocation,
}: LocationSelectorProps) {
  const t = useTranslations("Shop");
  const viewPort =
    locations.length > 0 ? calculateViewport(locations) : undefined;
  const [cameraProps, setCameraProps] = useState(viewPort);
  const selectedZipCode = useBoundStore.use.selectedZipCode();

  const handleCameraChange = useCallback((ev: { detail: CameraDetail }) => {
    setCameraProps(ev.detail);
  }, []);

  if (experityBooking) {
    return (
      <div className="w-full flex flex-col items-start gap-4 sm:px-2 lg:px-0">
        <p className="font-medium text-lg">{t("Location")}</p>
        <LocationZipSelector organizationAssets={organizationAssets} />
        {selectedZipCode && (
          <LocationListWithDistance
            experityBooking={experityBooking}
            organizationAssets={organizationAssets}
            zipCode={selectedZipCode}
            locations={locations}
            selectedPracticeId={selectedPracticeId}
            onSelectLocation={onSelectLocation}
            onLocationFocus={handleCameraChange}
            cameraProps={cameraProps}
            onCameraChange={handleCameraChange}
          />
        )}
      </div>
    );
  }

  const showMap = hasAnyMapCoordinates(locations);

  return (
    <div className="w-full flex flex-col items-start gap-4 sm:px-2 lg:px-0">
      <p className="font-medium text-lg">{t("Location")}</p>

      <div className="w-full flex lg:flex-row flex-col-reverse sm:justify-between border border-[#E1E1E1] rounded-2xl h-[400px] lg:h-[400px]">
        <LocationList
          experityBooking={experityBooking}
          locations={locations}
          selectedPracticeId={selectedPracticeId}
          onSelectLocation={onSelectLocation}
          onLocationFocus={handleCameraChange}
          showMap={showMap}
        />
        {showMap && (
          <LocationMap
            locations={locations}
            cameraProps={cameraProps}
            onCameraChange={handleCameraChange}
            onSelectLocation={onSelectLocation}
          />
        )}
      </div>
    </div>
  );
}

interface LocationZipSelectorProps {
  organizationAssets: OrganizationAssets | null | undefined;
}

function LocationZipSelector({
  organizationAssets,
}: Pick<LocationZipSelectorProps, "organizationAssets">) {
  const { primaryColor, secondaryColor } = organizationAssets ?? {};
  const selectedZipCode = useBoundStore.use.selectedZipCode();
  const [zipCodeInput, setZipCodeInput] = useState<string>(
    () => selectedZipCode ?? "",
  );
  const setSelectedZipCode = useBoundStore.use.setSelectedZipCode();
  const t = useTranslations("Shop");

  const handleSubmit = useCallback(() => {
    const zipCodeRegex = /^\d{5}$/;
    if (zipCodeRegex.test(zipCodeInput)) {
      setSelectedZipCode(zipCodeInput);
    }
  }, [zipCodeInput, setSelectedZipCode]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\D/g, "").slice(0, 5);
      setZipCodeInput(value);
    },
    [],
  );

  return (
    <div className="w-full flex flex-col items-start gap-6 bg-white rounded-2xl p-6 border border-gray-200">
      <p className="font-medium text-lg">
        {t("EnterYourZipCode", { orgName: "+MEDRITE" })}
      </p>

      <div className="w-full flex flex-row items-end gap-2">
        <div className="w-full flex flex-col gap-2">
          <Label className="text-sm ">
            {t("Zipcode")}{" "}
            <span style={{ color: secondaryColor || "#000000" }}>*</span>
          </Label>
          <Input
            type="text"
            placeholder={t("ZipcodePlaceholder")}
            value={zipCodeInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            className="w-full rounded-full border border-[#E1E1E1] px-6 py-4 h-12"
          />
        </div>
        <Button
          onClick={handleSubmit}
          disabled={zipCodeInput.length !== 5}
          className="rounded-full px-8 py-4 h-12"
          variant="default"
          style={{ backgroundColor: primaryColor || "#000000" }}
        >
          {t("Enter")}
        </Button>
      </div>
    </div>
  );
}

interface LocationListProps {
  experityBooking: boolean;
  organizationAssets?: OrganizationAssets | null | undefined;
  zipCode?: string;
  locations: Location[];
  selectedPracticeId: number;
  onSelectLocation: (id: number) => void;
  onLocationFocus: (ev: { detail: CameraDetail }) => void;
  disableAutoSelect?: boolean;
  // When false, the list takes full width and omits the right-edge
  // border/rounding overrides that exist to butt against the map panel.
  showMap?: boolean;
}

export function LocationList({
  experityBooking,
  organizationAssets,
  zipCode,
  locations,
  selectedPracticeId,
  onSelectLocation,
  onLocationFocus,
  disableAutoSelect = false,
  showMap = true,
}: LocationListProps) {
  const setPracticeId = useBoundStore.use.setPracticeId();
  const t = useTranslations("Shop");
  const { primaryColor, secondaryColor } = organizationAssets ?? {};
  // Track if user has explicitly selected a location when disableAutoSelect is true
  const hasExplicitSelection = useRef(false);

  const widthClasses = showMap
    ? "lg:w-[60%] lg:rounded-r-[0px] sm:rounded-r-2xl lg:border-r lg:border-r-[#E1E1E1]"
    : "w-full";

  if (experityBooking && locations.length === 0) {
    return (
      <div
        className={`${widthClasses} bg-white rounded-2xl sm:border-[0px] p-6`}
      >
        <p className="text-gray-500 text-center">
          {t("NoLocationsFoundForZipCode", { zipCode })}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`${widthClasses} bg-white rounded-2xl sm:border-[0px] overflow-y-auto`}
    >
      {locations.map((location, index) => {
        const [siteName, ...Address] = location.label.split("-");
        const locationId = Number.parseInt(location.value);
        // If disableAutoSelect is true, only show as selected if user has explicitly selected a location
        const isSelected = disableAutoSelect
          ? hasExplicitSelection.current && selectedPracticeId === locationId
          : selectedPracticeId === locationId;

        const match = location.googleMapsEmbed.match(coordPattern);
        const [, longitude, latitude] = match ?? [undefined, " ", " "];

        return (
          <LocationCard
            primaryColor={primaryColor || undefined}
            secondaryColor={secondaryColor || undefined}
            key={location.value}
            index={index}
            siteName={siteName}
            address={Address.join("-")}
            phoneNumber={location.phoneNumber ?? undefined}
            isSelected={isSelected}
            distance={location.distance}
            onSelect={() => {
              if (disableAutoSelect) {
                hasExplicitSelection.current = true;
              }
              setPracticeId(Number.parseInt(location.value));
              onSelectLocation(Number.parseInt(location.value));

              onLocationFocus({
                detail: {
                  center: {
                    lat: Number.parseFloat(latitude),
                    lng: Number.parseFloat(longitude),
                  },
                  zoom: 14,
                },
              });
            }}
          />
        );
      })}
    </div>
  );
}

export interface CameraDetail {
  center: { lat: number; lng: number };
  zoom: number;
}

interface LocationMapProps {
  zipCode?: string;
  locations: Location[];
  cameraProps?: CameraDetail;
  onCameraChange: (ev: { detail: CameraDetail }) => void;
  onSelectLocation: (id: number) => void;
}

interface LocationMapContentProps {
  zipCode?: string;
  locations: Location[];
  viewPort: { center: { lat: number; lng: number }; zoom: number };
  cameraProps?: CameraDetail;
  onCameraChange: (ev: { detail: CameraDetail }) => void;
  onSelectLocation: (id: number) => void;
}

function LocationMapContent({
  zipCode,
  locations,
  viewPort,
  cameraProps,
  onCameraChange,
  onSelectLocation,
}: LocationMapContentProps) {
  const zipCodeLocation = useZipCodeGeocode(zipCode);
  const lastFocusedZipCodeRef = useRef<string | undefined>(undefined);

  // Focus on zip code location when it becomes available
  // Using useEffect is necessary here to react to async geocoding completion
  useEffect(() => {
    if (!zipCodeLocation || !zipCode) {
      if (!zipCode) {
        lastFocusedZipCodeRef.current = undefined;
      }
      return;
    }

    // Only focus if this is a new zip code location
    if (lastFocusedZipCodeRef.current !== zipCode) {
      lastFocusedZipCodeRef.current = zipCode;
      onCameraChange({
        detail: {
          center: {
            lat: zipCodeLocation.lat,
            lng: zipCodeLocation.lng,
          },
          zoom: 14,
        },
      });
    }
  }, [zipCodeLocation, zipCode, onCameraChange]);

  return (
    <GoogleMap
      className="w-full h-full min-h-[150px] [&>div]:rounded-t-2xl [&>div]:lg:rounded-t-none [&>div]:lg:rounded-r-2xl [&>div]:lg:rounded-l-none"
      defaultZoom={viewPort.zoom}
      defaultCenter={viewPort.center}
      {...cameraProps}
      disableDefaultUI={true}
      mapId={"4fdb380eed181cb7"}
      gestureHandling={"greedy"}
      onCameraChanged={onCameraChange}
    >
      {zipCodeLocation && (
        <AdvancedMarker
          position={{
            lat: zipCodeLocation.lat,
            lng: zipCodeLocation.lng,
          }}
        >
          <Pin
            background={"#FF6B6B"}
            borderColor={"#C92A2A"}
            glyphColor={"#FFFFFF"}
          />
        </AdvancedMarker>
      )}
      {locations.map((location, index) => {
        const match = location.googleMapsEmbed.match(coordPattern);
        const [, longitude, latitude] = match ?? [undefined, " ", " "];

        return (
          match && (
            <AdvancedMarker
              position={{
                lat: Number.parseFloat(latitude),
                lng: Number.parseFloat(longitude),
              }}
              onClick={() => {
                onSelectLocation(Number.parseInt(location.value));
                onCameraChange({
                  detail: {
                    center: {
                      lat: Number.parseFloat(latitude),
                      lng: Number.parseFloat(longitude),
                    },
                    zoom: 14,
                  },
                });
              }}
            >
              <Pin
                background={"#01A0B5"}
                borderColor={"#006425"}
                glyphColor={"#60d98f"}
              >
                <p className="text-white text-xs"> {index + 1}</p>
              </Pin>
            </AdvancedMarker>
          )
        );
      })}
    </GoogleMap>
  );
}

export function LocationMap({
  zipCode,
  locations,
  cameraProps,
  onCameraChange,
  onSelectLocation,
}: LocationMapProps) {
  const viewPort =
    locations.length > 0 ? calculateViewport(locations) : undefined;

  if (!viewPort) return null;

  return (
    <div className="lg:w-[40%] w-full h-full lg:h-[400px] rounded-t-2xl lg:rounded-t-none lg:rounded-r-2xl lg:rounded-l-none overflow-hidden">
      <APIProvider apiKey={API_KEY} version="quarterly">
        <LocationMapContent
          zipCode={zipCode}
          locations={locations}
          viewPort={viewPort}
          cameraProps={cameraProps}
          onCameraChange={onCameraChange}
          onSelectLocation={onSelectLocation}
        />
      </APIProvider>
    </div>
  );
}
